import React, { useMemo, useState } from 'react';
import { EffectFade, Pagination } from 'swiper/modules';
import 'swiper/css/pagination';
import { Picture, Swiper } from 'common/components';
import { FreeMode, SwiperClass, SwiperSlide, Thumbs } from 'common/components/swiper';
import { VariantAxisProduct } from 'modules/collections';
import { createElementKey } from 'utils';
import { removeSpecialCharacters } from 'utils/format';
import { ImageZoom } from './image-zoom';
import { Preview } from './preview';
import { productImage, productMedia, productMediaEmpty, productMediaSwiper } from './product-media-styles';
import { PRODUCT_MEDIA_TRANSFORMATIONS } from './product-media-constants';

type ProductMediaProps = {
  collectionInfo: VariantAxisProduct;
  onPreviewClick: (imagePosition: number) => void;
  landingName: string;
};

export const ProductMedia: React.FC<ProductMediaProps> = ({
  collectionInfo,
  onPreviewClick,
  landingName,
}) => {
  const [thumbsSwiper, setThumbsSwiper] = useState<SwiperClass | null>(null);
  const [swiper, setSwiper] = useState<SwiperClass | null>(null);
  const [activeImageIndex, setActiveImageIndex] = useState(0);

  const imgAlt = useMemo(() => removeSpecialCharacters(collectionInfo.displayedName), [collectionInfo]);

  const handlePreviewSlideClick = (index: number) => () => {
    if (index === activeImageIndex) {
      return;
    }

    onPreviewClick(index);

    setActiveImageIndex(index);

    swiper?.slideTo(index);
    thumbsSwiper?.slideTo(index);
  };

  return (
    <div className={productMedia}>
      <Preview
        activeImageIndex={activeImageIndex}
        imgAlt={imgAlt}
        landingName={landingName}
        mediaPhoto={collectionInfo.mediaPhoto}
        onPreviewClick={handlePreviewSlideClick}
        setThumbsSwiper={setThumbsSwiper}
      />
      <ImageZoom
        imgAlt={imgAlt}
        imgSrc={`${landingName}${collectionInfo.mediaPhoto[activeImageIndex]}`}
        imgTransformations={PRODUCT_MEDIA_TRANSFORMATIONS}>
        <Swiper
          className={productMediaSwiper}
          breakpoints={{
            1024: { allowTouchMove: false },
          }}
          effect="fade"
          modules={[EffectFade, FreeMode, Pagination, Thumbs]}
          thumbs={{
            autoScrollOffset: 2,
            swiper: thumbsSwiper,
          }}
          onSwiper={setSwiper}
          pagination={{
            dynamicBullets: true,
          }}
          slidesPerView="auto"
          watchOverflow>
          {collectionInfo.mediaPhoto.map((image, index) => (
            <SwiperSlide key={createElementKey('image', index)}>
              <Picture
                alt={imgAlt}
                className={productImage}
                itemProp="image"
                src={`${landingName}${image}`}
                title={imgAlt}
                transformations={PRODUCT_MEDIA_TRANSFORMATIONS}
              />
            </SwiperSlide>
          ))}
          <div className={productMediaEmpty} />
        </Swiper>
      </ImageZoom>
    </div>
  );
};
